import type { AdminHealthResponse } from '@zinc/shared';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../../lib/api';
import { Panel, tableCell, tinyButton, when } from './parts';

const STATE: Record<string, string> = {
  queued: 'Queued',
  running: 'Running',
  done: 'Done',
  failed: 'Failed',
  paused: 'Paused',
};

/** Admin → Health: every kind of job, when it last ran, failures, and the latest jobs' logs. */
export function AdminHealthPage() {
  const data = useQuery({
    queryKey: ['admin', 'health'],
    queryFn: () => api<AdminHealthResponse>('/api/admin/health'),
    refetchInterval: 10_000,
  });
  const [open, setOpen] = useState<string | null>(null);
  const d = data.data;
  return (
    <>
      <h1 className="m-0 text-[26px] font-bold">Health</h1>
      {d?.worker && (
        <p className="m-0 text-[14px] text-muted">
          Worker started {when(d.worker.startedAt)} · solver {d.worker.solver ?? 'missing'}
        </p>
      )}
      <Panel title="Jobs">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[640px] border-collapse text-[13px]">
            <thead>
              <tr className="text-left text-muted">
                <th className={`${tableCell} font-semibold`}>Job</th>
                <th className={`${tableCell} font-semibold`}>When</th>
                <th className={`${tableCell} font-semibold`}>Last run</th>
                <th className={`${tableCell} text-right font-semibold`}>7 days</th>
                <th className={`${tableCell} font-semibold`}>Last failure</th>
              </tr>
            </thead>
            <tbody>
              {(d?.jobs ?? []).map((j) => (
                <tr key={j.type}>
                  <td className={`${tableCell} font-semibold`}>{j.name}</td>
                  <td className={tableCell}>{j.schedule}</td>
                  <td className={tableCell}>
                    {j.last
                      ? `${STATE[j.last.state] ?? j.last.state} · ${when(j.last.finishedAt ?? j.last.createdAt)}`
                      : 'never'}
                  </td>
                  <td className={`${tableCell} text-right tabular-nums`}>
                    {j.done} done{j.failed ? `, ${j.failed} failed` : ''}
                  </td>
                  <td className={`${tableCell} ${j.lastFailure ? 'text-mistake' : 'text-muted'}`}>
                    {j.lastFailure
                      ? `${when(j.lastFailure.finishedAt ?? j.lastFailure.createdAt)} · ${j.lastFailure.error ?? ''}`
                      : 'none'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>
      <Panel title="Latest jobs">
        <ul className="m-0 flex flex-col p-0">
          {(d?.recent ?? []).map((j) => (
            <li key={j.id} className="flex flex-col gap-1 border-b border-line-soft py-1.5">
              <div className="flex items-center gap-2 text-[13px]">
                <span className="w-40 shrink-0 font-semibold">{j.type}</span>
                <span className={j.state === 'failed' ? 'text-mistake' : 'text-muted'}>
                  {STATE[j.state] ?? j.state}
                </span>
                <span className="text-muted">{when(j.createdAt)}</span>
                <span className="min-w-0 flex-1 truncate text-mistake">{j.error ?? ''}</span>
                {j.log.length > 0 && (
                  <button
                    type="button"
                    className={tinyButton}
                    aria-expanded={open === j.id}
                    onClick={() => setOpen(open === j.id ? null : j.id)}
                  >
                    {open === j.id ? 'Hide log' : 'Log'}
                  </button>
                )}
              </div>
              {open === j.id && (
                <pre className="m-0 overflow-x-auto rounded-btn bg-field p-2 text-[12px] whitespace-pre-wrap">
                  {j.log.join('\n')}
                </pre>
              )}
            </li>
          ))}
        </ul>
      </Panel>
    </>
  );
}
